/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { ComponentType } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import uuid from 'uuid/v4';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePageState } from '@confluence/page-context';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';

import { FlagDescription } from './FlagDescription';
// Special Flags
import { initializeInfoFlag } from './InfoFlag';
import { initializeSuccessFlag } from './SuccessFlag';
// Helpers
import { ALLOWED_FLAG_MESSAGES } from './allowedFlagConstants';
import { buildEditorFlagMessage } from './buildEditorFlagMessage';
import type {
	EditorFlagsUserPersonalInformationQuery as EditorFlagsUserPersonalInformationQuery$data,
	EditorFlagsUserPersonalInformationQueryVariables as EditorFlagsUserPersonalInformationQuery$variables,
} from './__types__/EditorFlagsUserPersonalInformationQuery';
import type {
	EditorFlagsContentAncestorsQuery as EditorFlagsContentAncestorsQuery$data,
	EditorFlagsContentAncestorsQueryVariables as EditorFlagsContentAncestorsQuery$variables,
} from './__types__/EditorFlagsContentAncestorsQuery';
import { usePageConflictAnalytics } from './usePageConflictAnalytics';

export type EditorErrorFlagsProps = {
	errorData?: {
		[key: string]: any; // typical format is { "Error message": true } but additional data also supported
	};
	statusCode?: number;
	lastPublisher?: string;
	nextTryIn?: number;
	spaceKey?: string;
	parentPageId?: string;
	contentId?: string;
	flagData?: {
		[key: string]: any;
	};
	contentType?: string;
	collabService?: string;
	isUnpublishedDraft?: boolean;
};

export const EditorFlags: ComponentType<EditorErrorFlagsProps & WithFlagsProps> = injectIntl(
	({
		flags,
		nextTryIn,
		statusCode,
		flagData,
		errorData,
		intl: { formatMessage },
		lastPublisher,
		parentPageId,
		collabService,
		isUnpublishedDraft,
	}: EditorErrorFlagsProps & WrappedComponentProps & WithFlagsProps) => {
		const [{ contentSlug, contentId, spaceKey, contentType }] = usePageState();
		const isLivePage = useIsCurrentPageLive();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { triggerPageConflictAnalytics } = usePageConflictAnalytics({
			contentId,
			contentType,
			collabService,
			source: 'EditorFlags',
			actionSubjectId: 'editor.flag',
		});
		const userInfoQueryResult = useQuery<
			EditorFlagsUserPersonalInformationQuery$data,
			EditorFlagsUserPersonalInformationQuery$variables
		>(
			gql`
				query EditorFlagsUserPersonalInformationQuery($accountId: ID!) {
					user: confluence_atlassianUser(id: $accountId) {
						id
						displayName
					}
				}
			`,
			{
				variables: { accountId: lastPublisher || '' },
				// only make this query if the lastPublisher is defined and it is a publish/restore flag
				skip:
					!lastPublisher ||
					!errorData ||
					(!errorData[ALLOWED_FLAG_MESSAGES.OTHER_PUBLISH] &&
						!errorData[ALLOWED_FLAG_MESSAGES.OTHER_RESTORE]),
			},
		);

		// only make this query if the parentPageId is NOT defined and we have an error where we want link to the parent page
		const ancestorQuerySkipped =
			Boolean(parentPageId) ||
			!contentId ||
			!errorData ||
			!errorData[ALLOWED_FLAG_MESSAGES.PARENT_NOT_PUBLISHED];

		const ancestorQueryResult = useQuery<
			EditorFlagsContentAncestorsQuery$data,
			EditorFlagsContentAncestorsQuery$variables
		>(
			gql`
				query EditorFlagsContentAncestorsQuery($contentId: ID!) {
					content(id: $contentId, status: ["current", "draft"]) {
						nodes {
							id
							ancestors {
								id
							}
						}
					}
				}
			`,
			{
				variables: { contentId: contentId! },
				skip: ancestorQuerySkipped,
			},
		);

		const [actualParentPageId, setActualParentPageId] = useState<string | undefined>(parentPageId);

		useEffect(() => {
			// when done loading, set to the parentId that was found, or "" if not found
			if (!ancestorQuerySkipped && !ancestorQueryResult.loading) {
				setActualParentPageId(
					ancestorQueryResult.data?.content?.nodes?.[0]?.ancestors?.slice(-1)[0]?.id ||
						parentPageId ||
						'',
				);
			}
		}, [parentPageId, ancestorQueryResult, ancestorQuerySkipped]);

		// Cleanup on component unmount
		useEffect(() => () => void flags.hideAllFlags(), [flags]);

		useEffect(() => {
			if (
				!(
					errorData?.[ALLOWED_FLAG_MESSAGES.OTHER_PUBLISH] ||
					errorData?.[ALLOWED_FLAG_MESSAGES.OTHER_RESTORE] ||
					(errorData?.[ALLOWED_FLAG_MESSAGES.PARENT_NOT_PUBLISHED] &&
						actualParentPageId === undefined)
				) &&
				!flagData
			) {
				const flagContent = buildEditorFlagMessage({
					errorData,
					spaceKey,
					statusCode,
					formatMessage,
					nextTryIn,
					parentPageId: actualParentPageId,
					isUnpublishedDraft,
					isLivePage,
				});

				if (flagContent) {
					const flagId = uuid();
					void flags.showErrorFlag({
						id: flagId,
						title: flagContent.title,
						description: (
							<FlagDescription
								flags={flags}
								flagContent={flagContent}
								flagId={flagId}
								nextTryIn={nextTryIn}
							/>
						),
					});
					if (statusCode === 409) {
						triggerPageConflictAnalytics(
							Object.keys(errorData ?? {}).filter((k) =>
								k.startsWith(
									'com.atlassian.confluence.api.service.exceptions.ecosystem.ConflictException',
								),
							),
							{},
						);
					}
				} else {
					// If there is no errorData the value of flagProps is null
					// Clear the existing flags
					void flags.hideAllFlags();
				}
			}
		}, [
			isUnpublishedDraft,
			flags,
			flagData,
			nextTryIn,
			spaceKey,
			statusCode,
			errorData,
			lastPublisher,
			actualParentPageId,
			contentId,
			formatMessage,
			triggerPageConflictAnalytics,
			isLivePage,
		]);

		const showInfoFlag = useCallback(
			() => {
				const attributes = { contentType, spaceKey, contentId, contentSlug };
				return initializeInfoFlag(flags, formatMessage, attributes, isLivePage);
			},
			// Subscribe returns a new flag object each rerender causing infinite rerendering when displaying a publish flag,
			// so we are excluding it from the dep array.
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[formatMessage],
		);
		useEffect(() => {
			if (
				errorData &&
				(errorData[ALLOWED_FLAG_MESSAGES.OTHER_PUBLISH] ||
					errorData[ALLOWED_FLAG_MESSAGES.OTHER_RESTORE])
			) {
				showInfoFlag()(userInfoQueryResult, { ...errorData });
			}
		}, [userInfoQueryResult, errorData, showInfoFlag]);

		const showSuccessFlag = useCallback(() => {
			const attributes = { contentType, spaceKey, contentId, contentSlug };
			initializeSuccessFlag(flags, formatMessage, { attributes, flagData }, createAnalyticsEvent);
			// Subscribe returns a new flag object each rerender causing infinite re-rendering,
			// so we are excluding it from the dep array.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [formatMessage]);

		useEffect(() => {
			if (flagData) {
				showSuccessFlag();
			}
		}, [flagData, showSuccessFlag]);

		return ancestorQueryResult?.error ? <ErrorDisplay error={ancestorQueryResult.error} /> : null;
	},
);

// This will replace packages/confluence-fabric-editor/src/components/EditorErrorFlags/EditorErrorFlags.ts
// Once we switch to @confluence/full-page-editor
export const EditorErrorFlags = withFlags<EditorErrorFlagsProps>(EditorFlags);
